import type { StreamingEvent } from '../../types/chat.types';
import type { ContentProcessorState } from '../contentProcessor.types';

import {
  createRefStartDirective,
  createRefEndDirective,
  createRefGroupDirective,
  createLegacyRefDirective,
} from '../../utils/directives';
import { appendText } from '../nodes';

export function flushAccumulatedRefs(
  state: ContentProcessorState,
): ContentProcessorState {
  if (state.accumulatedRefIds.length === 0) {
    return state;
  }

  const markdown = createRefGroupDirective(state.accumulatedRefIds.join(','));

  return {
    ...appendText(state, markdown),
    accumulatedRefIds: [],
  };
}

export function handleReferenceStartEvent(
  state: ContentProcessorState,
  event: StreamingEvent<'ref_start'>,
): ContentProcessorState {
  const refId = event.data.reference_id;
  const markdown = createRefStartDirective(refId || '');

  return {
    ...appendText(state, markdown),
    openRefs: [...state.openRefs, refId],
    chunks: {
      ...state.chunks,
      progress: [],
      ref_start: [...state.chunks.ref_start, event.data],
    },
  };
}

export function handleReferenceGroupEvent(
  state: ContentProcessorState,
  event: StreamingEvent<'ref_group'>,
): ContentProcessorState {
  const refIds = event.data.reference_ids?.join(',') || '';
  const markdown = createRefGroupDirective(refIds);

  if (state.openRefs.length > 0) {
    return {
      ...state,
      deferredRefGroups: [...state.deferredRefGroups, markdown],
      accumulatedRefIds: [],
      chunks: {
        ...state.chunks,
        progress: [],
        ref_group: [...state.chunks.ref_group, event.data],
      },
    };
  }

  return {
    ...appendText(state, markdown),
    accumulatedRefIds: [],
    chunks: {
      ...state.chunks,
      progress: [],
      ref_group: [...state.chunks.ref_group, event.data],
    },
  };
}

export function handleReferenceEvent(
  state: ContentProcessorState,
  event: StreamingEvent<'ref'>,
): ContentProcessorState {
  const refId = event.data.reference_id ?? '';
  const hasRefStart = state.openRefs.includes(refId);

  const markdown = hasRefStart
    ? createRefEndDirective(refId)
    : createLegacyRefDirective(refId || 'error');

  let updatedState = appendText(state, markdown);
  let updatedOpenRefs = state.openRefs;

  if (hasRefStart) {
    updatedOpenRefs = state.openRefs.filter((id) => id !== refId);
  }

  let updatedDeferredRefGroups = state.deferredRefGroups;
  let updatedAccumulatedRefIds = state.accumulatedRefIds;

  if (hasRefStart && refId) {
    updatedAccumulatedRefIds = [...state.accumulatedRefIds, refId];
  }

  if (updatedOpenRefs.length === 0 && state.deferredRefGroups.length > 0) {
    const deferredDirectives = state.deferredRefGroups.join('');

    updatedState = appendText(updatedState, deferredDirectives);
    updatedDeferredRefGroups = [];
  }

  return {
    ...updatedState,
    openRefs: updatedOpenRefs,
    deferredRefGroups: updatedDeferredRefGroups,
    accumulatedRefIds: updatedAccumulatedRefIds,
    chunks: {
      ...state.chunks,
      progress: [],
      ref: [...state.chunks.ref, event.data],
    },
  };
}
